/**
 * LookupAdminAddRow — top-of-list "+ Add" affordance (Block 6.C.1).
 *
 * Compact: button "+ Add {label}". Tap → expands to inline form with
 * display_name input + slug auto-preview + Save / Cancel. Service-areas
 * variant adds area_type / state_code / parent_slug fields per ADR-020
 * §F-O-3 (Phase 1 floor: state-row select for parent).
 *
 * ADR-017: F1 (no modal); F2 (in-place); F4 (always-visible affordance);
 * F11 (inline-confirmation governance — same shape as LookupPicker's
 * "+ Add 'X' as new {type}" affordance).
 *
 * Per-form Save: multi-field coherence + inter-field validation +
 * undo-as-one-logical-unit (ADR-024 D-affiliation-editor-shape three-
 * criterion). Cancel reverts; Save commits via lookupsApi.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LookupTypeUrl, ServiceAreaLookupRow } from '@/lib/types';
import { lookupsApi, slugify } from '@/lib/lookups';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { SelectField } from '@/components/primitives/SelectField';
import { refreshFamily } from '@/lib/refreshFamily';

const VALID_AREA_TYPES = ['country', 'state', 'county', 'metro', 'region', 'zip'] as const;
type AreaType = (typeof VALID_AREA_TYPES)[number];

export interface LookupAdminAddRowProps {
  tenantSlug: string;
  type: LookupTypeUrl;
  /** Stable label for the affordance ("subcategory" / "specialty" / "service area"). */
  typeLabel: string;
  /** Cache key for the admin-list query so onSuccess invalidates cleanly. */
  invalidationKey: ReadonlyArray<unknown>;
}

export function LookupAdminAddRow({
  tenantSlug,
  type,
  typeLabel,
  invalidationKey,
}: LookupAdminAddRowProps): ReactNode {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="min-h-11 w-full text-left px-3 py-2 text-sm text-sage-800 border border-dashed border-sage-400/40 rounded-md hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        + Add {typeLabel}
      </button>
    );
  }

  if (type === 'service-areas') {
    return (
      <ServiceAreaAddForm
        tenantSlug={tenantSlug}
        invalidationKey={invalidationKey}
        typeLabel={typeLabel}
        onClose={() => setOpen(false)}
      />
    );
  }
  if (type === 'service-model') {
    // Phase 1.10 — Service Model add affordance deferred to a follow-up
    // sub-block. LookupAdmin.tsx gates this branch off via
    // `activeType !== 'service-model'` so this fallback should never
    // render in practice; defensive nullish-return keeps types tight.
    return null;
  }
  return (
    <SimpleAddForm
      tenantSlug={tenantSlug}
      type={type}
      typeLabel={typeLabel}
      invalidationKey={invalidationKey}
      onClose={() => setOpen(false)}
    />
  );
}

// ---------------------------------------------------------------------
// SimpleAddForm — subcategories + specialties (display_name + slug only)
// ---------------------------------------------------------------------

function SimpleAddForm({
  tenantSlug,
  type,
  typeLabel,
  invalidationKey,
  onClose,
}: {
  tenantSlug: string;
  type: 'subcategories' | 'specialties' | 'highlights';
  typeLabel: string;
  invalidationKey: ReadonlyArray<unknown>;
  onClose: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();

  const slug = slugify(displayName.trim());

  // The name, the lookup type, its slug and the list to refresh (the
  // family) are read at the press and travel as the variables.
  const mutation = useMutation({
    mutationFn: async (v: { input: { display_name: string }; type: typeof type; slug: string; family: readonly unknown[] }) => {
      if (v.type === 'subcategories') {
        return lookupsApi.addSubcategory(tenantSlug, v.input);
      }
      if (v.type === 'specialties') {
        return lookupsApi.addSpecialty(tenantSlug, v.input);
      }
      return lookupsApi.addHighlight(tenantSlug, v.input);
    },
    onSuccess: async (_res, v) => {
      await queryClient.invalidateQueries({ queryKey: v.family });
      onClose();
    },
    onError: (e: Error & { status?: number; body?: { error?: string; slug?: string } }, v) => {
      if (e.status === 409 && e.body?.error === 'lookup_slug_conflict') {
        setError(`The short name "${e.body.slug ?? v.slug}" is already used. Choose a different one.`);
      } else {
        setError(pageWriteText(e, contact));
      }
    },
  });

  function handleSave(): void {
    setError(null);
    const trimmed = displayName.trim();
    if (trimmed.length === 0) {
      setError('Enter a display name.');
      return;
    }
    mutation.mutate({ input: { display_name: trimmed }, type, slug, family: refreshFamily(invalidationKey) });
  }

  return (
    <div className="border border-sage-400/40 bg-sage-50 rounded-md p-3 mb-2">
      <div className="text-sm font-semibold mb-2">Add {typeLabel}</div>
      <label className="block text-xs text-ink-soft mb-1">Display name</label>
      <input
        type="text"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        autoFocus
        aria-label={`New ${typeLabel} display name`}
        disabled={mutation.isPending}
        className="w-full min-h-11 px-3 py-2 mb-2 border border-sage-400/30 rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      />
      {displayName.trim().length > 0 && (
        <div className="text-xs text-ink-soft mb-2">
          Slug will be: <span className="font-mono">{slug || '(invalid)'}</span>
        </div>
      )}
      {error !== null && (
        <div role="alert" className="text-xs text-red-700 mb-2" data-testid="lookup-add-error">
          ⚠ {error}
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="min-h-11 px-3 py-1 text-sm text-ink-soft hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={mutation.isPending || displayName.trim().length === 0 || slug.length === 0}
          className="min-h-11 px-3 py-1 text-sm bg-sage-800 text-cream rounded-md hover:bg-forest-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          {mutation.isPending ? 'Adding…' : '+ Add'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// ServiceAreaAddForm — display_name + slug + area_type + state_code + parent_slug
// ---------------------------------------------------------------------

function ServiceAreaAddForm({
  tenantSlug,
  invalidationKey,
  typeLabel,
  onClose,
}: {
  tenantSlug: string;
  invalidationKey: ReadonlyArray<unknown>;
  typeLabel: string;
  onClose: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [areaType, setAreaType] = useState<AreaType>('county');
  const [stateCode, setStateCode] = useState('');
  const [parentSlug, setParentSlug] = useState<string | ''>('');
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();

  // Fetch existing state-typed rows for the parent picker (Phase 1 floor
  // per ADR-020 §F-O-3: dropdown of existing state-typed rows).
  const stateRowsQuery = useQuery<{ rows: ServiceAreaLookupRow[] }>({
    queryKey: ['service-area-state-rows', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listServiceAreas(tenantSlug, { area_type: 'state', limit: 50 }, signal),
  });

  const slug = slugify(displayName.trim());

  // The area, its slug and the list to refresh (the family) are read at
  // the press and travel as the variables.
  const mutation = useMutation({
    mutationFn: async (v: {
      input: {
        display_name: string;
        area_type: AreaType;
        state_code: string | null;
        parent_slug: string | null;
      };
      slug: string;
      family: readonly unknown[];
    }) => lookupsApi.addServiceArea(tenantSlug, v.input),
    onSuccess: async (_res, v) => {
      await queryClient.invalidateQueries({ queryKey: v.family });
      // Also invalidate the state-rows fixture if a state was added.
      await queryClient.invalidateQueries({ queryKey: ['service-area-state-rows', tenantSlug] });
      onClose();
    },
    onError: (e: Error & { status?: number; body?: { error?: string; slug?: string; parent_slug?: string } }, v) => {
      if (e.status === 409 && e.body?.error === 'lookup_slug_conflict') {
        setError(`The short name "${e.body.slug ?? v.slug}" is already used. Choose a different one.`);
      } else if (e.status === 400 && e.body?.error === 'lookup_parent_not_found') {
        setError(`The parent area "${e.body.parent_slug ?? v.input.parent_slug ?? ''}" can't be found. Pick a parent area from the list.`);
      } else {
        setError(pageWriteText(e, contact));
      }
    },
  });

  function handleSave(): void {
    setError(null);
    const trimmedName = displayName.trim();
    if (trimmedName.length === 0) {
      setError('Enter a display name.');
      return;
    }
    const trimmedState = stateCode.trim().toUpperCase();
    if (areaType !== 'country' && trimmedState.length !== 2) {
      setError('State code must be 2 letters.');
      return;
    }
    mutation.mutate({
      input: {
        display_name: trimmedName,
        area_type: areaType,
        state_code: areaType === 'country' ? null : trimmedState,
        parent_slug: parentSlug === '' ? null : parentSlug,
      },
      slug,
      family: refreshFamily(invalidationKey),
    });
  }

  const stateRows = stateRowsQuery.data?.rows ?? [];

  return (
    <div className="border border-sage-400/40 bg-sage-50 rounded-md p-3 mb-2">
      <div className="text-sm font-semibold mb-2">Add {typeLabel}</div>

      <label className="block text-xs text-ink-soft mb-1">Display name</label>
      <input
        type="text"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        autoFocus
        aria-label="New service area display name"
        disabled={mutation.isPending}
        className="w-full min-h-11 px-3 py-2 mb-2 border border-sage-400/30 rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      />

      <label className="block text-xs text-ink-soft mb-1">Area type</label>
      <SelectField size="default"
        value={areaType}
        onChange={(e) => setAreaType(e.target.value as AreaType)}
        aria-label="Area type"
        disabled={mutation.isPending}
        className="w-full mb-2"
      >
        {VALID_AREA_TYPES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </SelectField>

      {areaType !== 'country' && (
        <>
          <label className="block text-xs text-ink-soft mb-1">State code (2-letter)</label>
          <input
            type="text"
            value={stateCode}
            onChange={(e) => setStateCode(e.target.value.toUpperCase().slice(0, 2))}
            aria-label="State code"
            placeholder="NJ"
            disabled={mutation.isPending}
            className="w-full min-h-11 px-3 py-2 mb-2 border border-sage-400/30 rounded-md text-sm uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          />
        </>
      )}

      <label className="block text-xs text-ink-soft mb-1">Parent area</label>
      <SelectField size="default"
        value={parentSlug}
        onChange={(e) => setParentSlug(e.target.value)}
        aria-label="Parent area slug"
        disabled={mutation.isPending || stateRowsQuery.isLoading}
        className="w-full mb-2"
      >
        <option value="">(none: top-level area)</option>
        {stateRows.map((r) => (
          <option key={r.id} value={r.slug}>
            {r.display_name} ({r.slug})
          </option>
        ))}
      </SelectField>

      {displayName.trim().length > 0 && (
        <div className="text-xs text-ink-soft mb-2">
          Slug will be: <span className="font-mono">{slug || '(invalid)'}</span>
        </div>
      )}

      {error !== null && (
        <div role="alert" className="text-xs text-red-700 mb-2" data-testid="lookup-add-error">
          ⚠ {error}
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="min-h-11 px-3 py-1 text-sm text-ink-soft hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={mutation.isPending || displayName.trim().length === 0 || slug.length === 0}
          className="min-h-11 px-3 py-1 text-sm bg-sage-800 text-cream rounded-md hover:bg-forest-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          {mutation.isPending ? 'Adding…' : '+ Add'}
        </button>
      </div>
    </div>
  );
}
